import { useTranslation } from 'react-i18next'

import Icon from '../../../../ui/Icon'
import { ListCardStripe } from '../../../../../types/listing'
import { ListRow } from '../../../../listItem/listRow/ListRow'
import { ListCard } from '../../../../listItem/listCard/ListCard'
import { ListingDetails } from '../../../../listing/ListingDetails'

import { CONFIG } from '../../../../../config'
import { DisplayType } from '../../../../../types/listing'
import { AccountActionEntry } from '../../../../../types/audit/audit'
import { AccountActionType } from '../../../../../types/user/userProfile'
import {
  displayDate,
  displayLocalDateTimeHourMinute,
} from '../../../../../utils/dateUtils'

const ACTION_BADGE_CLASS: Record<AccountActionType, string> = {
  [AccountActionType.LOCK]: 'badge badge-danger',
  [AccountActionType.UNLOCK]: 'badge badge-success',
  [AccountActionType.RESET_PASSWORD]: 'badge badge-info',
}

const ACTION_STRIPES: Record<AccountActionType, ListCardStripe> = {
  [AccountActionType.LOCK]: 'danger',
  [AccountActionType.UNLOCK]: 'success',
  [AccountActionType.RESET_PASSWORD]: 'info',
}

const EMPTY = <span className="muted">&mdash;</span>

export const AccountActionList = () => {
  const { t, i18n } = useTranslation(['audit', 'users'])

  const renderItem = (item: AccountActionEntry, viewMode: DisplayType) => {
    const date = `${displayDate(item.createdDate, i18n.language)} ${displayLocalDateTimeHourMinute(item.createdDate)}`
    const action = (
      <span className={ACTION_BADGE_CLASS[item.type]}>
        {t(`audit.actions.type.${item.type}`)}
      </span>
    )
    const reason = item.reason
      ? t(`users:users.lock-confirmation.reasons.${item.reason}`)
      : EMPTY
    const performedBy = item.performedBy ?? t('audit.actions.system')

    if (viewMode === DisplayType.LIST) {
      return (
        <ListRow
          key={item.id}
          cells={[
            { key: 'date', className: 'mono', content: date },
            { key: 'user', content: item.email },
            { key: 'action', content: action },
            { key: 'reason', content: reason },
            { key: 'details', content: item.details || EMPTY },
            { key: 'performed-by', content: performedBy },
          ]}
        />
      )
    }

    return (
      <ListCard
        key={item.id}
        stripe={ACTION_STRIPES[item.type]}
        title={item.email}
        subtitle={
          <>
            <Icon name="clock" size={12} />
            <span>{date}</span>
          </>
        }
        status={action}
        fields={[
          { key: 'reason', label: t('audit.actions.columns.reason'), value: reason, mono: false },
          { key: 'performed-by', label: t('audit.actions.columns.performed-by'), value: performedBy, mono: false },
          ...(item.details
            ? [
                {
                  key: 'details',
                  label: t('audit.actions.columns.details'),
                  value: item.details,
                  mono: false,
                  wide: true,
                },
              ]
            : []),
        ]}
      />
    )
  }

  return (
    <ListingDetails<AccountActionEntry>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/audit/account-actions`}
      initialViewMode={DisplayType.LIST}
      displaySearch
      searchPlaceholder={t('audit.search-placeholder')}
      tableColumns={[
        { key: 'date', label: t('audit.actions.columns.date') },
        { key: 'user', label: t('audit.actions.columns.user') },
        { key: 'action', label: t('audit.actions.columns.action') },
        { key: 'reason', label: t('audit.actions.columns.reason') },
        { key: 'details', label: t('audit.actions.columns.details') },
        { key: 'performed-by', label: t('audit.actions.columns.performed-by') },
      ]}
      renderItem={renderItem}
    />
  )
}
